/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/SnippetInput" />
import {
    AzureRMConnectionType,
    ConnectedServicePrefix,
    DataSourcePrefix,
    InternalDataSources
} from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { IServiceConnectionProvider, YamlSnippetValidatorInput, YamlSnippetValidatorResult } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { AzureSubscriptionPickList } from "Build/DesignerExtensions/YamlSnippetForm/Components/AzureSubscriptionPickList";
import { DataSourcePickList } from "Build/DesignerExtensions/YamlSnippetForm/Components/DataSourcePickList";
import { EnhancedDropdown } from "Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedDropdown";
import { EnhancedLabel } from "Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedLabel";
import { RadioGroup } from "Build/DesignerExtensions/YamlSnippetForm/Components/RadioGroup";
import { ServiceConnectionPickList } from "Build/DesignerExtensions/YamlSnippetForm/Components/ServiceConnectionPickList";
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";
import { InputTypes } from "Pipelines/Common/Library/Contracts/Constants";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";

export interface SnippetInputProps {
    snippetInput: YamlSnippetInput;
    isVisible: boolean;
    serviceConnectionProvider: IServiceConnectionProvider;
    onValueChanged(name: string, value: string, valueAlias?: string): void;
    getValue(name: string): string;
    onError(location: string, error: Error): void;
}

export function unsupportedInputs(input: YamlSnippetInput[]): string[] {
    const unsupportedInputs: string[] = [];

    input.forEach(input => {
        if (
            input.type === InputTypes.BOOL ||
            input.type === InputTypes.STRING ||
            input.type === InputTypes.PICKLIST ||
            input.type === InputTypes.RADIO ||
            input.type === InputTypes.INT ||
            input.type === InputTypes.MULTILINE ||
            input.type.indexOf(DataSourcePrefix) === 0 ||
            input.type.indexOf(ConnectedServicePrefix) === 0 ||
            InternalDataSources[input.type]
        ) {
            return;
        }

        unsupportedInputs.push(input.type);
    });

    return unsupportedInputs;
}

export class SnippetInput extends React.Component<SnippetInputProps, {}> {
    public render(): JSX.Element | null {
        if (!this.props.isVisible) {
            return null;
        }

        const snippetInput = this.props.snippetInput;
        const value = this.props.getValue(snippetInput.name);

        let validatorResult : YamlSnippetValidatorResult = {
            message: "",
            isValid: true
        } as YamlSnippetValidatorResult;

        if ("validation" in snippetInput && typeof (snippetInput as YamlSnippetValidatorInput).validation === "function") {
            const validatorSnippetInput = snippetInput as YamlSnippetValidatorInput;
            validatorResult = validatorSnippetInput.validation();
        }

        switch (snippetInput.type) {
            case InputTypes.BOOL: {
                // Invalid values disable the checkbox so they are not overridden.
                const isValidValue = value === snippetInput.defaultValue || value === "true" || value === "false";

                let documentation = snippetInput.helpMarkdown;
                if (!isValidValue) {
                    documentation = `> ${format(Resources.InvalidBooleanValue, value)}\n\n${documentation}`;
                }

                return (
                    <span className="inline-flex-row flex-center">
                        <Checkbox
                            className={css(snippetInput.required && "required", "yaml-snippet-checkbox")}
                            label={snippetInput.label}
                            checked={value === "true"}
                            disabled={!isValidValue}
                            onChange={(_e, checked) => this.props.onValueChanged(snippetInput.name, checked ? "true" : "false")}
                        />
                        {/* checkbox just has a lot of problems with a button inside its label so lets place it adjacent instead of inside */}
                        <EnhancedLabel title={""} isRequired={snippetInput.required} documentation={documentation} />
                    </span>
                );
            }
            case InputTypes.FILEPATH:
            case InputTypes.STRING:
            case InputTypes.INT: {
                return (
                    <FormItem
                        error={!validatorResult.isValid}
                        severity={validatorResult.severity}
                        message={validatorResult.message}
                        label={
                            <EnhancedLabel title={snippetInput.label} isRequired={snippetInput.required} documentation={snippetInput.helpMarkdown} />
                        }
                    >
                        <TextField value={value} required={snippetInput.required} onChange={(e, value) => this.props.onValueChanged(snippetInput.name, value)} autoFocus={validatorResult.shouldFocusOnElement} />
                    </FormItem>
                );
            }
            case InputTypes.MULTILINE: {
                return (
                    <FormItem
                        label={
                            <EnhancedLabel title={snippetInput.label} isRequired={snippetInput.required} documentation={snippetInput.helpMarkdown} />
                        }
                    >
                        <TextField
                            className={"snippet-multiline"}
                            value={value}
                            required={snippetInput.required}
                            multiline={true}
                            autoAdjustHeight={true}
                            rows={3}
                            onChange={(e, value) => this.props.onValueChanged(snippetInput.name, value)}
                        />
                    </FormItem>
                );
            }
            case InputTypes.PICKLIST: {
                return (
                    <EnhancedDropdown
                        inputDefinition={this.props.snippetInput}
                        getValue={this.props.getValue}
                        onValueChanged={this.props.onValueChanged}
                    />
                );
            }
            case InputTypes.RADIO: {
                return <RadioGroup inputDefinition={snippetInput} onValueChanged={this.props.onValueChanged} getValue={this.props.getValue} />;
            }
        }

        if (snippetInput.type.indexOf(DataSourcePrefix) === 0 || InternalDataSources[snippetInput.type]) {
            return (
                <DataSourcePickList
                    inputDefinition={snippetInput}
                    serviceConnectionProvider={this.props.serviceConnectionProvider}
                    onValueChanged={this.props.onValueChanged}
                    getValue={this.props.getValue}
                    onError={this.props.onError}
                />
            );
        }

        if (snippetInput.type.indexOf(ConnectedServicePrefix) === 0) {
            const connectionType = snippetInput.type.substr(ConnectedServicePrefix.length).toLowerCase();
            switch (connectionType) {
                case AzureRMConnectionType: {
                    return (
                        <AzureSubscriptionPickList
                            inputDefinition={snippetInput}
                            serviceConnectionProvider={this.props.serviceConnectionProvider}
                            getValue={this.props.getValue}
                            onValueChanged={this.props.onValueChanged}
                            onError={this.props.onError}
                        />
                    );
                }
                default: {
                    return (
                        <ServiceConnectionPickList
                            inputDefinition={snippetInput}
                            connectionType={connectionType}
                            serviceConnectionProvider={this.props.serviceConnectionProvider}
                            getValue={this.props.getValue}
                            onValueChanged={this.props.onValueChanged}
                            onError={this.props.onError}
                        />
                    );
                }
            }
        }

        // by default, support things as a textbox that the user can type whatever they need to into it
        return (
            <FormItem
                label={<EnhancedLabel title={snippetInput.label} isRequired={snippetInput.required} documentation={snippetInput.helpMarkdown} />}
            >
                <TextField value={value} required={snippetInput.required} onChange={(e, value) => this.props.onValueChanged(snippetInput.name, value)} />
            </FormItem>
        );
    }
}
